import type { DataSourceId } from '@shared/data-sources';
import type { LiveChangeMessage, LiveRecordMessage } from '@shared/types/live';
import { invalidateLoaders } from '@/hooks/loader-store';
import { invalidateDataFreshness } from '@/hooks/use-data-freshness';

/**
 * What the team hub's `data` and `record` messages do in this tab (data-freshness.mdc, "Data changed
 * by someone else"). `TaskLiveConnector` hands them here; pages listen with `useRecordChanges`.
 */

/**
 * Loader keys (after `<teamId>:`) whose data comes from a source, reloaded behind what they show when
 * its sync ends. Marketing pages need none: they reload when the freshness check reports a newer
 * sync (`useMarketingLoader`).
 */
const LOADER_PREFIXES_BY_SOURCE: Partial<Record<DataSourceId, readonly string[]>> = {
  shopify: ['report:'],
  xero: ['report:'],
  // Reports on marketing data reload when it syncs.
  google_ads: ['report:wasted-ad-spend:', 'report:google-ads-check:'],
  microsoft_ads: ['report:wasted-ad-spend:', 'report:google-ads-check:'],
  meta_ads: ['report:meta-ads-check:', 'report:google-ads-check:'],
  ga4: ['report:wasted-ad-spend:', 'report:google-ads-check:'],
  // Its Monthly searches column (T-445).
  search_volumes: ['report:wasted-ad-spend:'],
};

/** The loader key prefixes a sync of these sources makes out of date. */
export function loaderPrefixesForSources(teamId: string, sources: readonly DataSourceId[]): string[] {
  const prefixes = new Set<string>();
  for (const source of sources) {
    for (const prefix of LOADER_PREFIXES_BY_SOURCE[source] ?? []) prefixes.add(`${teamId}:${prefix}`);
  }
  return [...prefixes];
}

/** A `record` message, or null after the socket came back (changes may have been missed). */
type RecordListener = (message: LiveRecordMessage | null) => void;
const recordListeners = new Set<RecordListener>();

/** Hear `record` messages (`useRecordChanges` does this for a page). Returns the unsubscribe. */
export function subscribeRecordChanges(listener: RecordListener): () => void {
  recordListeners.add(listener);
  return () => {
    recordListeners.delete(listener);
  };
}

/** A `data` or `record` message arrived on the team hub. */
export function receiveLiveChange(teamId: string, message: LiveChangeMessage): void {
  if (message.type === 'data') {
    invalidateDataFreshness(teamId);
    for (const prefix of loaderPrefixesForSources(teamId, message.sources ?? [])) {
      invalidateLoaders(prefix);
    }
    return;
  }
  tellRecordListeners(message);
}

function tellRecordListeners(message: LiveRecordMessage | null): void {
  for (const listener of [...recordListeners]) {
    try {
      listener(message);
    } catch (error) {
      console.error('[live] a record change listener failed', error);
    }
  }
}

/**
 * The socket came back after being down, so messages may have been missed: freshness is checked
 * again, reports reload behind their rows, and pages listening for records reload unless they have
 * unsaved edits.
 */
export function resyncLiveChanges(teamId: string): void {
  invalidateDataFreshness(teamId);
  // A sync may have ended meanwhile: reports on screen load again behind their rows.
  for (const prefix of loaderPrefixesForSources(teamId, ['shopify', 'xero'])) invalidateLoaders(prefix);
  tellRecordListeners(null);
}
